import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private readonly baseUrl: string;

  constructor(private http: HttpClient) {
    this.baseUrl = environment.apiUrl;
  }

  /**
   * GET request
   */
  get<T>(endpoint: string, params?: any): Observable<T> {
    let httpParams = new HttpParams();

    if (params) {
      Object.keys(params).forEach((key) => {
        if (params[key] !== null && params[key] !== undefined) {
          httpParams = httpParams.append(key, params[key].toString());
        }
      });
    }

    return this.http.get<T>(`${this.baseUrl}/${endpoint}`, {
      params: httpParams,
    });
  }

  /**
   * POST request (opcional: headers extras, params na query string).
   */
  post<T>(
    endpoint: string,
    body: any,
    options?: { headers?: Record<string, string>; params?: Record<string, string | number> }
  ): Observable<T> {
    const headers = options?.headers ? new HttpHeaders(options.headers) : undefined;
    let params = new HttpParams();
    let hasParams = false;
    if (options?.params) {
      Object.keys(options.params).forEach((key) => {
        const v = options!.params![key];
        if (v !== null && v !== undefined) {
          params = params.set(key, String(v));
          hasParams = true;
        }
      });
    }
    return this.http.post<T>(`${this.baseUrl}/${endpoint}`, body, {
      ...(headers ? { headers } : {}),
      ...(hasParams ? { params } : {}),
    });
  }

  /**
   * POST que espera 204 No Content (sem body). Evita erro de parse JSON em resposta vazia.
   */
  postVoid(endpoint: string, body: any): Observable<void> {
    return this.http.post(`${this.baseUrl}/${endpoint}`, body, { responseType: 'text' }).pipe(
      map(() => undefined)
    );
  }

  /**
   * PUT request
   */
  put<T>(endpoint: string, body: any): Observable<T> {
    return this.http.put<T>(`${this.baseUrl}/${endpoint}`, body);
  }

  /**
   * PATCH request
   */
  patch<T>(endpoint: string, body: any): Observable<T> {
    return this.http.patch<T>(`${this.baseUrl}/${endpoint}`, body);
  }

  /**
   * DELETE request
   */
  delete<T>(endpoint: string): Observable<T> {
    return this.http.delete<T>(`${this.baseUrl}/${endpoint}`);
  }

  /**
   * DELETE que espera 204 No Content (sem body).
   */
  deleteVoid(endpoint: string): Observable<void> {
    return this.http.delete(`${this.baseUrl}/${endpoint}`, { responseType: 'text' }).pipe(
      map(() => undefined)
    );
  }

  /**
   * PUT que espera 204 No Content (sem body).
   */
  putVoid(endpoint: string, body: any): Observable<void> {
    return this.http.put(`${this.baseUrl}/${endpoint}`, body, { responseType: 'text' }).pipe(
      map(() => undefined)
    );
  }

  /**
   * Upload file
   */
  uploadFile<T>(
    endpoint: string,
    file: File,
    additionalData?: any
  ): Observable<T> {
    const formData = new FormData();
    formData.append('file', file);

    if (additionalData) {
      Object.keys(additionalData).forEach((key) => {
        formData.append(key, additionalData[key]);
      });
    }

    return this.http.post<T>(`${this.baseUrl}/${endpoint}`, formData);
  }
}
